import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Play, ArrowLeft } from "lucide-react";
import { Reveal, ImageReveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { founder, brand, isPlaceholder } from "@/config/site";

const EASE = [0.22, 1, 0.36, 1];

/**
 * FounderStory — two-state media card (photo ↔ video) that swaps in place.
 * • Initial: founder portrait + name/role + "▶ Ver nuestra historia" pill.
 * • Click: the frame transforms into an inline <video> occupying the exact
 *   same rectangle — no modal, no route change, no layout shift.
 * • Escape key + explicit "← Back to photo" button return to the portrait.
 * • Dot indicators (● ○) also toggle both states.
 * • Video is lazy: <video> is only mounted when the user opts in, so nothing
 *   downloads on page load.
 */
export default function FounderStory() {
  const { t, lang } = useLanguage();
  const reduce = useReducedMotion();
  const hasName = !isPlaceholder(founder.name);
  const hasPortrait = !!founder.portrait;
  const role = founder.role?.[lang] || founder.role?.en || "";

  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);

  const goToVideo = () => setShowVideo(true);
  const goToPhoto = () => {
    const v = videoRef.current;
    if (v) { try { v.pause(); } catch (_) {} }
    setShowVideo(false);
  };

  // Escape key returns to photo.
  useEffect(() => {
    if (!showVideo) return;
    const onKey = (e) => { if (e.key === "Escape") goToPhoto(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showVideo]);

  // Pause the video when it leaves the viewport (uses IntersectionObserver).
  const frameRef = useRef(null);
  useEffect(() => {
    if (!showVideo) return;
    const el = frameRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const v = videoRef.current;
          if (!v) continue;
          if (!e.isIntersecting) { try { v.pause(); } catch (_) {} }
          else if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
        }
      },
      { threshold: [0, 0.4] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [showVideo]);

  const ctaText = lang === "es" ? "Ver nuestra historia" : "Watch our story";
  const backText = lang === "es" ? "Volver a la foto" : "Back to photo";
  const videoAria = lang === "es" ? "Ver video de nuestra historia" : "Watch our story video";

  return (
    <section id="our-story" data-testid="founder-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-5 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-5 lg:gap-16">

          {/* ── Portrait ↔ Video (same rectangle, same rounded corners) ── */}
          <div className="lg:col-span-2">
            <div
              ref={frameRef}
              data-testid="founder-media"
              className="relative mx-auto aspect-[4/5] max-w-sm overflow-hidden rounded-2xl border border-[#eee] bg-[#faf8f5]"
            >
              <AnimatePresence mode="wait" initial={false}>
                {!showVideo ? (
                  <motion.div
                    key="photo"
                    initial={reduce ? { opacity: 0 } : { opacity: 0, x: -20 }}
                    animate={reduce ? { opacity: 1 } : { opacity: 1, x: 0 }}
                    exit={reduce ? { opacity: 0 } : { opacity: 0, x: 20 }}
                    transition={{ duration: 0.42, ease: EASE }}
                    className="absolute inset-0"
                  >
                    <ImageReveal className="h-full w-full">
                      {hasPortrait ? (
                        <picture>
                          <source srcSet={founder.portrait} type="image/webp" />
                          <img
                            data-testid="founder-portrait"
                            src={founder.portraitFallback || founder.portrait}
                            alt={hasName ? `${founder.name} — ${role}` : brand.name}
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover"
                          />
                        </picture>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#f2f2ef] to-[#e6e6e1]">
                          <span className="font-serif-display text-6xl text-[#c9c9c2]">K</span>
                        </div>
                      )}
                    </ImageReveal>
                  </motion.div>
                ) : (
                  <motion.div
                    key="video"
                    initial={reduce ? { opacity: 0 } : { opacity: 0, x: 20 }}
                    animate={reduce ? { opacity: 1 } : { opacity: 1, x: 0 }}
                    exit={reduce ? { opacity: 0 } : { opacity: 0, x: -20 }}
                    transition={{ duration: 0.42, ease: EASE }}
                    className="absolute inset-0 bg-black"
                  >
                    <video
                      ref={videoRef}
                      data-testid="founder-video-el"
                      src="/videos/moissanite-hero.mp4"
                      poster="/images/founder-kelo-diamonds.jpg"
                      preload="metadata"
                      autoPlay
                      muted
                      playsInline
                      controls
                      className="h-full w-full object-cover"
                      onEnded={goToPhoto}
                    />
                    {/* Back-to-photo button (top-left, sits above controls) */}
                    <button
                      type="button"
                      onClick={goToPhoto}
                      aria-label={backText}
                      data-testid="founder-back-btn"
                      className="press absolute left-3 top-3 z-10 inline-flex h-11 min-w-[44px] items-center gap-1.5 rounded-full border border-white/25 bg-black/55 px-3 font-body text-xs font-medium text-white backdrop-blur-md transition-colors hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
                      <span>{backText}</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Dot indicators (● ○) */}
              <div
                data-testid="founder-dots"
                className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex items-center justify-center gap-2"
              >
                <button
                  type="button"
                  aria-label={lang === "es" ? "Ver foto" : "Show photo"}
                  aria-current={!showVideo}
                  onClick={goToPhoto}
                  data-testid="founder-dot-photo"
                  className="pointer-events-auto press h-2 w-2 rounded-full transition-all"
                  style={{
                    background: !showVideo ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.45)",
                    boxShadow: !showVideo ? "0 0 0 3px rgba(0,0,0,0.18)" : "0 0 0 2px rgba(0,0,0,0.14)",
                    transform: !showVideo ? "scale(1.15)" : "scale(1)",
                  }}
                />
                <button
                  type="button"
                  aria-label={lang === "es" ? "Ver video" : "Show video"}
                  aria-current={showVideo}
                  onClick={goToVideo}
                  data-testid="founder-dot-video"
                  className="pointer-events-auto press h-2 w-2 rounded-full transition-all"
                  style={{
                    background: showVideo ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.45)",
                    boxShadow: showVideo ? "0 0 0 3px rgba(0,0,0,0.18)" : "0 0 0 2px rgba(0,0,0,0.14)",
                    transform: showVideo ? "scale(1.15)" : "scale(1)",
                  }}
                />
              </div>
            </div>

            {/* Name + role + CTA */}
            {hasName && (
              <Reveal delay={0.4} className="mt-5 text-center lg:text-left">
                <p data-testid="founder-name" className="font-serif-display text-lg text-[#111]">{founder.name}</p>
                {role && (
                  <p className="mt-0.5 font-body text-xs uppercase tracking-[0.24em] text-[#999]">
                    {role}
                  </p>
                )}
                {!showVideo && (
                  <button
                    type="button"
                    onClick={goToVideo}
                    aria-label={videoAria}
                    data-testid="founder-play-cta"
                    className="story-play-cta group mt-4 inline-flex items-center gap-2 rounded-full bg-[#111] px-5 py-2.5 font-body text-sm font-medium text-white shadow-[0_6px_20px_rgba(0,0,0,0.18)] transition-transform hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]/40 active:scale-[0.97]"
                  >
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/12 transition-transform group-hover:scale-110">
                      <Play className="h-3 w-3 translate-x-[1px] fill-white" strokeWidth={0} />
                    </span>
                    {ctaText}
                  </button>
                )}
              </Reveal>
            )}
          </div>

          {/* ── Story text ────────────────────────────────────────────── */}
          <div className="lg:col-span-3">
            <Reveal>
              <span className="eyebrow text-[11px] text-[#999]">{t.founder.eyebrow}</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-3 font-serif-display text-3xl font-medium leading-tight text-[#111] sm:text-4xl">
                {t.founder.title}
              </h2>
            </Reveal>
            {t.founder.body.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.05}>
                <p className="mt-4 max-w-xl font-body text-sm leading-relaxed text-[#666]">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <p className="mt-6 font-serif-display text-base italic text-[#333]">
                {hasName ? `— ${founder.name}, ${brand.short}` : t.founder.signature}
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
